import axios from "./axios";

/**
 * Build request payload for get-quote-data: one object per script with exchange, segment, symbol, option_type, strike_price, expiry_date.
 * expiry_date must be YYYY-MM-DD (API format). Used for dropdown options (can have empty values).
 */
export function buildRequestQuoteDataList(scriptSelections) {
  return (scriptSelections || []).map((s) => ({
    exchange: s.exchange ?? "",
    segment: s.segment ?? "",
    symbol: s.symbol ?? "",
    option_type: s.option_type ?? "",
    strike_price: s.strike_price ?? "",
    expiry_date: s.expiry_date ?? "",
  }));
}

/**
 * Check if a script selection is complete (has all required fields).
 * For EQ (equity), expiry_date is optional; for F&O it is required.
 */
export function isScriptSelectionComplete(s) {
  const ex = (s?.exchange ?? "").toString().trim();
  const seg = (s?.segment ?? "").toString().trim();
  const sym = (s?.symbol ?? "").toString().trim();
  const opt = (s?.option_type ?? "").toString().trim();
  const strike = s?.strike_price;
  const numStrike = strike === "" || strike == null ? 0 : Number(strike);
  const exp = (s?.expiry_date ?? "").toString().trim();
  const isEq = String(opt).toUpperCase() === "EQ";
  return ex && seg && sym && opt && !Number.isNaN(numStrike) && (exp || isEq);
}

/**
 * Build request list for get_quote_data: ONLY include complete scripts (filters out incomplete).
 * strike_price as number.
 */
export function buildQuoteDataRequestList(scriptSelections) {
  return (scriptSelections || [])
    .filter(isScriptSelectionComplete)
    .map((s) => ({
      exchange: (s.exchange ?? "").toString().trim(),
      segment: (s.segment ?? "").toString().trim(),
      symbol: (s.symbol ?? "").toString().trim(),
      option_type: (s.option_type ?? "").toString().trim(),
      strike_price: s.strike_price === "" || s.strike_price == null ? 0 : Number(s.strike_price),
      expiry_date: (s.expiry_date ?? "").toString().trim(),
    }));
}

/**
 * Fetch dropdown options for order initiation.
 * GET /api/broker-gateway/get-quote-data/?request_quote_data_list=[...]&type=get_quote_drop_down_options
 * @param {Array} requestQuoteDataList - Array of { exchange, segment, symbol, option_type, strike_price, expiry_date } (one per script)
 * @param {Object} options - { signal } for abort on unmount
 * @returns {Promise} response.data with quote_drop_down_options_list
 */
export const getOrderInitiationConfig = async (requestQuoteDataList, options = {}) => {
  const { signal } = options;
  const list = Array.isArray(requestQuoteDataList) && requestQuoteDataList.length > 0
    ? requestQuoteDataList
    : [{ exchange: "", segment: "", symbol: "", option_type: "", strike_price: "", expiry_date: "" }];
  const response = await axios.get("/api/broker-gateway/get-quote-data/", {
    params: {
      request_quote_data_list: JSON.stringify(list),
      type: "get_quote_drop_down_options"
    },
    signal
  });
  return response.data;
};

/**
 * Fetch quote data (LTP, depth, OHLC, etc.) for selected scripts.
 * GET /api/broker-gateway/get-quote-data/?request_quote_data_list=[...]&type=get_quote_data
 * Call when scriptSelections have values from dropdowns; response.quote_data_list maps 1:1 to request list.
 * @param {Array} requestQuoteDataList - Array of { exchange, segment, symbol, option_type, strike_price (number), expiry_date }
 * @param {Object} options - { signal } for abort
 * @returns {Promise} response.data with quote_data_list
 */
export const getQuoteData = async (requestQuoteDataList, options = {}) => {
  const { signal } = options;
  if (!Array.isArray(requestQuoteDataList) || requestQuoteDataList.length === 0) {
    return { quote_data_list: [] };
  }
  const response = await axios.get("/api/broker-gateway/get-quote-data/", {
    params: {
      request_quote_data_list: JSON.stringify(requestQuoteDataList),
      type: "get_quote_data"
    },
    signal
  });
  return response.data;
};

/**
 * Fetch all baskets with their broker_client_codes.
 * GET /api/broker-gateway/baskets/
 * Response: { response: [{ id, basket_name, description, broker_client_codes: string[] }] }
 */
export const getBaskets = async (options = {}) => {
  const { signal } = options;
  const response = await axios.get(`/api/broker-gateway/baskets/`, { signal });
  return response.data;
};

/**
 * Fetch all clients with their margin data (called once on page load).
 * GET /api/broker-gateway/get-clients-with-margin/
 * Response: [{ id, broker_client_code, available_margin }]
 */
export const getClientsWithMargin = async (options = {}) => {
  const { signal } = options;
  const response = await axios.get(`/api/broker-gateway/get-clients-with-margin/`, { signal });
  return response.data;
};

export const getOrderInitiationScripInfo = async (requestScripList, underlyingToken, options = {}) => {
  const { signal } = options;
  const response = await axios.get("/api/broker-gateway/get-order-initiation-scrip-info/", {
    params: {
      request_scrip_list: JSON.stringify(requestScripList),
      request_underlying_instrument_token: underlyingToken
    },
    signal
  });
  return response.data;
};

/**
 * Place order: POST /api/broker-gateway/order-initiate/
 * @param {Array} scripList - [{ trading_symbol, zerodha_instrument_token, order_quantity, disclosed_quantity, transaction_type, product_type, order_type, order_price, trigger_price, lot_size, exchange, strategy_name, validity, order_slice_type, order_slice_value, sweep_order, time_interval }, ...]
 * @param {Object} clientMultipliers - { [clientCode]: multiplierNumber, ... }
 */
export const orderInitiate = async (scripList, clientMultipliers, options = {}) => {
  const { signal } = options;
  const payload = { scrip_list: scripList, client_multipliers: clientMultipliers };
  const response = await axios.post("/api/broker-gateway/order-initiate/", payload, { signal });
  return response.data;
};

/**
 * Fetch available strategies.
 * GET /api/strategy/strategies/
 * @returns {Promise} Array of { id, master, strategy_name, strategy_type, description, created_at }
 */
export const getStrategies = async (options = {}) => {
  const { signal } = options;
  const response = await axios.get("/api/strategy/strategies/", { signal });
  return response.data;
};
